import { ApolloClient, InMemoryCache, HttpLink, ApolloLink, CombinedGraphQLErrors } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { fetchAuthSession } from 'aws-amplify/auth';

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_GRAPHQL_ENDPOINT || 'https://zx2l2cfkgzerzaobtoppnuss3u.appsync-api.us-east-1.amazonaws.com/graphql',
});

// Error handling link for debugging
const errorLink = onError(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach((err) => {
      console.error(`[GraphQL error]: Message: ${err.message}, Location: ${JSON.stringify(err.locations)}, Path: ${err.path}, Operation: ${operation.operationName}`);
    });
  } else if (error) {
    console.error(`[Network error]: ${error.message}, Operation: ${operation.operationName}`);
  }
});

// WebSocket link for real-time subscriptions
const wsLink = new GraphQLWsLink(createClient({
  url: import.meta.env.VITE_GRAPHQL_WS_ENDPOINT || 'wss://zx2l2cfkgzerzaobtoppnuss3u.appsync-realtime-api.us-east-1.amazonaws.com/graphql',
  connectionParams: async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (token) {
        return {
          Authorization: token,
        };
      } else {
        return {
          'x-api-key': import.meta.env.VITE_API_KEY || 'da2-biizixnvi5dv5mp6fu76fx6eay',
        };
      }
    } catch {
      return {
        'x-api-key': import.meta.env.VITE_API_KEY || 'da2-biizixnvi5dv5mp6fu76fx6eay',
      };
    }
  },
}));

// Split link to route queries/mutations to HTTP and subscriptions to WebSocket
const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);

const authLink = setContext(async (operation, { headers }) => {
  const operationName = operation.operationName || 'unknown';
  console.log(`[Apollo] Processing request for: ${operationName}`);
  
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    
    if (token) {
      console.log(`[Apollo] Using auth token for: ${operationName}`);
      return {
        headers: {
          ...headers,
          Authorization: token,
        },
      };
    } else {
      console.log(`[Apollo] No auth token - using API key for: ${operationName}`);
      return {
        headers: {
          ...headers,
          'x-api-key': import.meta.env.VITE_API_KEY || 'da2-biizixnvi5dv5mp6fu76fx6eay',
        },
      };
    }
  } catch {
    console.log(`[Apollo] No auth session - using API key for: ${operationName}`);
    return {
      headers: {
        ...headers,
        'x-api-key': import.meta.env.VITE_API_KEY || 'da2-biizixnvi5dv5mp6fu76fx6eay',
      },
    };
  }
});

export const createApolloClient = () => {
  return new ApolloClient({
    link: ApolloLink.from([errorLink, authLink, splitLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            callLogs: {
              keyArgs: ['filters'],
              merge(existing = [], incoming) {
                return [...existing, ...incoming];
              },
            },
            inquiries: {
              keyArgs: ['filters'],
              merge(existing = [], incoming) {
                return [...existing, ...incoming];
              },
            },
            messagesByConversation: {
              keyArgs: ['conversationId'],
              merge(existing = [], incoming) {
                return [...existing, ...incoming];
              },
            },
          },
        },
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
      query: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
    devtools: { enabled: true },
  });
};

// Real-time connection manager
export class RealtimeConnectionManager {
  private subscriptions: Map<string, { unsubscribe: () => void }> = new Map();

  constructor(_client: ApolloClient) {
    // Client stored for potential future use
  }

  async connect(): Promise<void> {
    console.log('Real-time connection established');
  }

  disconnect(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
    this.subscriptions.clear();
  }

  isConnected(): boolean {
    return true;
  }

  subscribeToUpdates(subscriptionId: string, subscription: { unsubscribe: () => void }): void {
    this.subscriptions.set(subscriptionId, subscription);
  }

  unsubscribe(subscriptionId: string): void {
    const subscription = this.subscriptions.get(subscriptionId);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(subscriptionId);
    }
  }

  handleConnectionLoss(): void {
    console.log('Real-time connection lost, attempting to reconnect...');
  }

  async reconnect(): Promise<void> {
    console.log('Reconnecting to real-time services...');
  }
}
